import { onCleanup, onMount, Show, type JSX } from "solid-js"
import { createStore } from "solid-js/store"

// One V2 menu at a time, fixed to the viewport under its trigger (Source files, Chats). Focus moves in on open,
// arrows / Home / End move, Esc and a click away close it and return focus to the trigger (Review R5, R6).
// `kind` names which menu of the page is open; `id` the row it belongs to. `left` kinds open from the trigger's left
// edge, the rest from its right edge; `side` kinds open beside the trigger, past its right edge (the 48px rail's menus).
export type MenuState<K extends string> = { kind: K; id?: string; top: number; right: number; left?: number }

export function useMenu<K extends string>(options: { class: string; left?: (kind: K) => boolean; side?: (kind: K) => boolean }) {
  const [state, setState] = createStore({ menu: undefined as MenuState<K> | undefined })
  let root: HTMLDivElement | undefined
  let trigger: HTMLElement | undefined
  const items = () => [...(root?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled), [role="menuitemradio"]:not(:disabled)') ?? [])]
  const close = (focus = false) => {
    if (!state.menu) return
    setState("menu", undefined)
    if (focus) trigger?.focus({ preventScroll: true })
  }
  const open = (kind: K, button: HTMLElement, id?: string) => {
    if (state.menu?.kind === kind && state.menu.id === id) return close(true)
    trigger = button
    const rect = button.getBoundingClientRect()
    setState("menu", options.side?.(kind)
      ? { kind, id, top: rect.top - 4, right: 0, left: rect.right + 14 }
      : { kind, id, top: rect.bottom + 4, right: window.innerWidth - rect.right, left: options.left?.(kind) ? rect.left : undefined })
    // A choice menu (Source files' period) opens on the checked item.
    requestAnimationFrame(() => (root?.querySelector<HTMLElement>('[aria-checked="true"]') ?? items()[0] ?? root)?.focus({ preventScroll: true }))
  }
  const onKeyDown = (event: KeyboardEvent) => {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return
    event.preventDefault()
    const list = items()
    if (!list.length) return
    const index = list.indexOf(document.activeElement as HTMLElement)
    const next = event.key === "Home" ? 0 : event.key === "End" ? list.length - 1 : (index + (event.key === "ArrowDown" ? 1 : -1) + list.length) % list.length
    list[next]?.focus({ preventScroll: true })
  }
  // Tab (or anything else) taking focus out of the menu closes it; its trigger is its own (the click toggles).
  const onFocusOut = (event: FocusEvent) => {
    const next = event.relatedTarget
    if (next instanceof Node && (root?.contains(next) || trigger?.contains(next))) return
    close()
  }
  onMount(() => {
    const inside = (target: EventTarget | null) => target instanceof Node && (!!root?.contains(target) || !!trigger?.contains(target))
    const away = (event: PointerEvent) => {
      if (!state.menu || inside(event.target)) return
      close()
      // A click on nothing focusable leaves focus on the page: back to the trigger.
      requestAnimationFrame(() => { if (!document.activeElement || document.activeElement === document.body) trigger?.focus({ preventScroll: true }) })
    }
    const escape = (event: KeyboardEvent) => {
      if (!state.menu || event.key !== "Escape" || event.defaultPrevented) return
      event.preventDefault()
      close(true)
    }
    // The menu is fixed to where its trigger was: a page scroll closes it, a scroll inside it does not.
    const scrolled = (event: Event) => { if (!(event.target instanceof Node && root?.contains(event.target))) close() }
    const resized = () => close()
    document.addEventListener("pointerdown", away)
    document.addEventListener("keydown", escape)
    window.addEventListener("resize", resized)
    window.addEventListener("scroll", scrolled, true)
    onCleanup(() => {
      document.removeEventListener("pointerdown", away)
      document.removeEventListener("keydown", escape)
      window.removeEventListener("resize", resized)
      window.removeEventListener("scroll", scrolled, true)
    })
  })
  // mousedown kept off the items: focus stays on the open item, so the menu's focusout does not close it before the click.
  const Panel = (props: { label?: string; children: (menu: () => MenuState<K>) => JSX.Element }) => (
    <Show when={state.menu}>{(menu) => (
      <div ref={root} class={options.class} role="menu" aria-label={props.label} tabindex="-1" onKeyDown={onKeyDown} onFocusOut={onFocusOut} onMouseDown={(event) => event.preventDefault()}
        style={menu().left !== undefined ? { top: `${menu().top}px`, left: `${menu().left}px` } : { top: `${menu().top}px`, right: `${menu().right}px` }}>
        {props.children(menu)}
      </div>
    )}</Show>
  )
  return { menu: () => state.menu, open, close, trigger: () => trigger, Panel }
}
